import React, { ReactNode } from 'react';
import {
  Box,
  Flex,
  Heading,
  HStack,
  Button,
  Icon,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  useColorModeValue,
  Text,
  Image,
  Spacer,
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { FiRefreshCw, FiUploadCloud, FiDownloadCloud, FiInfo, FiLogOut } from 'react-icons/fi';
import { Link, useLocation } from 'react-router-dom';

// Types
interface AdminLayoutProps {
  children: ReactNode;
  title: string;
  isLoading?: boolean;
  onRefresh?: () => void;
  onSyncToFirebase?: () => void;
  onSyncToCodebase?: () => void;
  onTestConnection?: () => void;
  onTestIntegration?: () => void;
  onLogout?: () => void;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({
  children,
  title,
  isLoading = false,
  onRefresh,
  onSyncToFirebase,
  onSyncToCodebase,
  onTestConnection,
  onTestIntegration,
  onLogout,
}) => {
  // Methodist brand colors
  const methodistBlue = '#0066CC';
  const methodistNavy = '#002D72';
  const methodistGreen = '#38A169';
  const methodistPurple = '#805AD5';
  
  const location = useLocation();
  
  return (
    <Box minH="100vh" bg={useColorModeValue('#f8f9fa', '#1a202c')} overflowY="auto" overflowX="hidden">
      <Flex direction="column" h="100vh">
        {/* Header */}
        <Flex
          as="header"
          align="center"
          justify="space-between"
          py={2}
          px={4}
          bg={methodistNavy}
          color="white"
          position="sticky"
          top={0}
          zIndex={10}
          boxShadow="sm"
        >
          <Flex align="center">
            <Image src="/assets/houston-methodist-official-logo.png" alt="Houston Methodist" height="30px" mr={3} />
            <Heading size="md" color="white">Administration</Heading>
          </Flex>
          
          <HStack spacing={4}>
            {/* Navigation Links */}
            <Link to="/admin/tts-studio">
              <Button 
                variant={location.pathname === '/admin/tts-studio' ? "solid" : "ghost"}
                colorScheme="whiteAlpha"
                size="sm"
              >
                TTS Studio
              </Button>
            </Link>
            <Link to="/admin/media">
              <Button 
                variant={location.pathname === '/admin/media' ? "solid" : "ghost"}
                colorScheme="whiteAlpha"
                size="sm"
              >
                Media
              </Button>
            </Link>
            
            <Spacer minW="20px" />
            
            {onRefresh && (
              <Button 
                leftIcon={<Icon as={FiRefreshCw} />} 
                bg={methodistBlue}
                color="white"
                size="sm" 
                onClick={onRefresh}
                isLoading={isLoading}
              >
                Refresh
              </Button>
            )}
            
            {(onSyncToFirebase || onSyncToCodebase || onTestConnection || onTestIntegration) && (
              <Menu>
                <MenuButton as={Button} rightIcon={<ChevronDownIcon />} bg={methodistPurple} color="white" size="sm">
                  Sync Options
                </MenuButton>
                <MenuList>
                  {onSyncToFirebase && (
                    <MenuItem 
                      icon={<Icon as={FiUploadCloud} />} 
                      onClick={onSyncToFirebase}
                      isDisabled={isLoading}
                    >
                      Sync Codebase → Firebase
                    </MenuItem>
                  )}
                  {onSyncToCodebase && (
                    <MenuItem 
                      icon={<Icon as={FiDownloadCloud} />} 
                      onClick={onSyncToCodebase}
                      isDisabled={isLoading}
                    >
                      Sync Firebase → Codebase
                    </MenuItem>
                  )}
                  {(onTestConnection || onTestIntegration) && <MenuDivider />}
                  {onTestConnection && (
                    <MenuItem 
                      icon={<Icon as={FiInfo} />} 
                      onClick={onTestConnection}
                    >
                      Test Firebase Connection
                    </MenuItem>
                  )}
                  {onTestIntegration && (
                    <MenuItem 
                      icon={<Icon as={FiInfo} />} 
                      onClick={onTestIntegration}
                    >
                      Test Firebase Integration
                    </MenuItem>
                  )}
                </MenuList>
              </Menu>
            )}
            
            {onLogout && (
              <Button 
                leftIcon={<Icon as={FiLogOut} />}
                bg="red.500" 
                color="white" 
                size="sm" 
                onClick={onLogout}
                _hover={{ bg: 'red.600' }}
              >
                Logout
              </Button>
            )}
          </HStack>
        </Flex>
        
        {/* Page Title */}
        <Flex justify="flex-start" align="center" mb={1} mt={1} px={4}>
          <Box>
            <Text fontSize="lg" fontWeight="medium" color="gray.700">{title}</Text>
          </Box>
        </Flex>
        
        {/* Main Content */}
        <Box flex="1" overflowY="auto" overflowX="hidden" p={1} bg="#f8f9fa">
          <Box maxW="100%" mx="auto" bg="white" borderRadius="md" boxShadow="sm" p={1}>
            {children}
          </Box>
        </Box>
      </Flex>
    </Box>
  );
};

export default AdminLayout;
